import * as vp from 'vopay';
import * as Yup from 'yup';
import moment from 'moment';
import { FunctionComponent, JSX, useEffect } from 'react';
import { Formik } from 'formik';
import { CreateAutoBalanceHandler, CreateAutoBalanceRequest } from './types';
import { buildCreateAutoBalanceRows } from './buildCreateAutoBalanceTransferRows.helper';
import styles from './CreateAutoBalanceTransferForm.module.scss';
import { usePendingData } from '../../../../providers/PendingData.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useTranslation } from 'react-i18next';

const { Box, Table, TextField, Select, Icon, Description, DatepickerSimple } = vp;

type CreateAutoBalanceProps = {
  handlers: CreateAutoBalanceHandler;
  defaultValues: CreateAutoBalanceRequest;
};

type FC = FunctionComponent<CreateAutoBalanceProps>;

const CreateAutoBalanceTransferForm: FC = (props: CreateAutoBalanceProps) => {
  const { handlers, defaultValues } = props;
  const {
    response: { Country, clientAccountsEnabled },
  } = useClientData();
  const createAutoBalanceRow = buildCreateAutoBalanceRows(Country, clientAccountsEnabled);
  const now = moment().format('YYYY-MM-DD');
  const { initPendingData, updatePendingData } = usePendingData();

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.Create.Form' });

  return (
    <Formik
      initialValues={defaultValues}
      enableReinitialize
      onSubmit={handlers.onReview}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit, submitForm }) => {
        useEffect(() => {
          handlers.onAutoBalanceChange(values, errors, touched, submitForm, createAutoBalanceRow);
        }, [values, errors]);
        useEffect(() => {
          initPendingData({ ...values, Balance: values.Balance?.toString() || '0' });
        }, []);
        useEffect(() => {
          updatePendingData({ ...values, Balance: values.Balance?.toString() || '0' });
        }, [values]);

        const balanceTransferTable: vp.TableProps = {
          name: 'fund-account-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: createAutoBalanceRow.map(
            ({
              label,
              id,
              require,
              preset,
              type,
              placeholder,
              getSelectOptions,
              allowNewOption,
              disabled,
              instructions,
            }) => {
              let fieldComponent;

              const fieldLabel = (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {label} {getRequiredIcon(require)}
                  </Box>
                  {instructions && (
                    <Box maxwidth="340px">
                      <Description size="md">{instructions}</Description>
                    </Box>
                  )}
                </Box>
              );

              if (type === 'input') {
                fieldComponent = (
                  <TextField
                    preset={preset}
                    key={id}
                    name={id}
                    variant="standard"
                    value={String(values[id] || '')}
                    placeholder={placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={disabled}
                    error={errors[id] && touched[id] ? errors[id] : null}
                  />
                );
              } else if (type === 'singleDate') {
                fieldComponent = (
                  <div className={styles.datePickerContainer}>
                    <DatepickerSimple
                      name={id}
                      value={String(values[id])}
                      error={errors[id] && touched[id] ? errors[id] : null}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        if (e.target && e.target.value === 'undefined') {
                          e.target.value = String(values[id]);
                        }
                        handleChange(e);
                      }}
                    />
                  </div>
                );
              } else
                fieldComponent = (
                  <Select
                    variant="standard"
                    options={getSelectOptions()}
                    key={id}
                    name={id}
                    value={String(values[id])}
                    placeholder={placeholder}
                    noOrdering
                    onBlur={handleBlur}
                    onChange={handleChange}
                    allowNew={allowNewOption}
                    error={errors[id] && touched[id] ? errors[id] : null}
                  />
                );
              return {
                Field: fieldLabel,
                Input: fieldComponent,
              };
            },
          ),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Table {...balanceTransferTable} />
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      Currency: Yup.string().required(),
      Balance: Yup.string()
        .test('amount-validation', t('ErrorMessage.amountMinValue'), (value) => {
          const amountValidation = Number(value) <= 0.0;
          return !amountValidation;
        })
        .required(),
      StartDate: Yup.string()
        .test(
          'start-date-validation',
          t('ErrorMessage.invalidStartDate', { startDate: moment(now).format('YYYY-MM-DD') }),
          (value: string | undefined) => {
            const dateValidation = moment(Number(value)).format('YYYY-MM-DD') < moment(now).format('YYYY-MM-DD');
            return !dateValidation;
          },
        )
        .required(),
      Frequency: Yup.string().required(),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default CreateAutoBalanceTransferForm;
